import React, { useEffect, useState } from "react";
import { Button, CloseButton, Modal, Row, Col, Alert } from "react-bootstrap";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import axios from "axios";

interface IProps {
  closeFilterModal: () => void;
  selectedSentFilterAndValue: any;
  setSelectedSentFilterAndValue: any;
  handleSubmit: (selectedSentFilterAndValueValues: any) => void;
}

const SentFilterModal: React.FC<IProps> = ({
  closeFilterModal,
  selectedSentFilterAndValue,
  setSelectedSentFilterAndValue,
  handleSubmit,
}) => {
  const [selectSentFilters, setSelectSentFilters] = useState<string[]>([]);
  const [dateTo, setDateTo] = useState<string>("");
  const [suggestions, setSuggestions] = useState([]);
  const [assigneeSuggestions, setAssigneeSuggestions] = useState([]);
  const [error, setError] = useState<string | null>(null);

  const filters = [
    { label: "Customer email", value: "action__contact__email__icontains" },
    { label: "Agent name", value: "action__assignee__username__icontains" },
    { label: "Subject", value: "original_subject__icontains" },
    { label: "Date", value: "created" },
  ];

  // Load filter state from sessionStorage when the component mounts
  useEffect(() => {
    const savedFilters = sessionStorage.getItem("selectSentFilters");
    const savedFilterValues = sessionStorage.getItem(
      "selectedSentFilterAndValue"
    );

    let parsedFilters = [];
    let parsedFilterValues: any = {};
    if (
      savedFilters &&
      savedFilters !== "undefined" &&
      savedFilters !== "null"
    ) {
      parsedFilters = JSON.parse(savedFilters);
    }
    if (
      savedFilterValues &&
      savedFilterValues !== "undefined" &&
      savedFilterValues !== "null"
    ) {
      parsedFilterValues = JSON.parse(savedFilterValues);
    }
    if (parsedFilters.length && Object.keys(parsedFilterValues).length) {
      setSelectSentFilters(parsedFilters);
      setSelectedSentFilterAndValue(parsedFilterValues);
      setDateTo(parsedFilterValues.created ?? "");
    } else {
      const safeSelectedSentFilterAndValue = selectedSentFilterAndValue || {};
      const appliedFilters = Object.keys(safeSelectedSentFilterAndValue).filter(
        (key) => safeSelectedSentFilterAndValue[key]
      );
      setSelectSentFilters(appliedFilters);
      setDateTo(safeSelectedSentFilterAndValue.created || "");
    }
  }, [setSelectedSentFilterAndValue]);

  // Save filter state to sessionStorage when it changes
  useEffect(() => {
    sessionStorage.setItem(
      "selectSentFilters",
      JSON.stringify(selectSentFilters)
    );
    sessionStorage.setItem(
      "selectedSentFilterAndValue",
      JSON.stringify(selectedSentFilterAndValue)
    );
  }, [selectSentFilters, selectedSentFilterAndValue]);

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSelectSentFilters((prevSelectedFilters) => {
      let updatedFilters;
      if (prevSelectedFilters.includes(value)) {
        updatedFilters = prevSelectedFilters.filter(
          (filter) => filter !== value
        );
        setSelectedSentFilterAndValue((prev) => ({
          ...prev,
          [value]: "",
        }));
      } else {
        updatedFilters = [...prevSelectedFilters, value];
      }
      handleSubmit(
        updatedFilters.reduce((acc, filter) => {
          acc[filter] = (selectedSentFilterAndValue ?? {})[filter] || "";
          return acc;
        }, {})
      );
      return updatedFilters;
    });
  };

  const handleFilterValueChange = (value: string, filter: string) => {
    setSelectedSentFilterAndValue((prevFilterValues) => ({
      ...prevFilterValues,
      [filter]: value,
    }));
  };

  const handleDateToChange = (event: any) => {
    const value = event.target.value;
    setDateTo(value);
    setSelectedSentFilterAndValue((prev) => ({
      ...prev,
      created: value,
    }));
  };

  const resetFilters = () => {
    setSelectSentFilters([]);
    const emptyFilters = filters.reduce((acc, filter) => {
      acc[filter.value] = "";
      return acc;
    }, {});
    setSelectedSentFilterAndValue(emptyFilters);
    setDateTo("");
    setError(null);
    sessionStorage.removeItem("selectSentFilters");
    sessionStorage.removeItem("selectedSentFilterAndValue");
    handleSubmit({});
  };

  const isFilterApplied = () =>
    selectSentFilters.some(
      (filter) =>
        (filter === "created" && dateTo) || selectedSentFilterAndValue[filter]
    );

  const apiPort = process.env.REACT_APP_API_PROTOCOL;
  const fetchingTenant = window.location.hostname.split(".")[0];

  const searchCustomerEmail = (skey: string) => {
    axios
      .get(
        `${apiPort}://${fetchingTenant}.${process.env.REACT_APP_BASE_URL}/v1/customers/contacts/search-by-email/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("auth-token")}`,
          },
          params: { email: skey },
        }
      )
      .then((res) => {
        if (res.status === 200) {
          setSuggestions(res.data);
          setError(null);
        }
      })
      .catch(() => {
        setError("Failed to fetch customer email suggestions.");
      });
  };

  const searchAssigneeSuggestions = (skey: string) => {
    axios
      .get(
        `${apiPort}://${fetchingTenant}.${process.env.REACT_APP_BASE_URL}/v1/users/users/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("auth-token")}`,
          },
          params: { username__icontains: skey },
        }
      )
      .then((res) => {
        if (res.status === 200) {
          setAssigneeSuggestions(res.data);
          setError(null);
        }
      })
      .catch(() => {
        setError("Failed to fetch assignee suggestions.");
      });
  };

  const handleApplyFilters = () => {
    handleSubmit(selectedSentFilterAndValue);
    closeFilterModal();
  };

  return (
    <Modal show={true} onHide={closeFilterModal} centered>
      <Modal.Header>
        <Modal.Title className="fw-bold">Filters</Modal.Title>
        <CloseButton onClick={closeFilterModal} />
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>} {/* Display error */}
        <div className="px-3 py-2">
          {filters.map((filter) => (
            <Row key={filter.value} className="mb-3 align-items-center">
              <Col xs={12} md={4} className="text-nowrap">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectSentFilters.includes(filter.value)}
                      onChange={handleFilterChange}
                      value={filter.value}
                      color="primary"
                    />
                  }
                  label={filter.label}
                />
              </Col>
              <Col xs={12} md={8}>
                {selectSentFilters.includes(filter.value) && (
                  <>
                    {filter.value === "created" ? (
                      <TextField
                        id="date-to"
                        label="Select Date"
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          inputProps: {
                            style: { textTransform: "uppercase" }, // Optional: uppercase input text
                          },
                        }}
                        fullWidth
                        value={dateTo}
                        onChange={handleDateToChange}
                      />
                    ) : filter.value === "action__contact__email__icontains" ? (
                      <Autocomplete
                        freeSolo
                        options={suggestions.map((option) => option.email)}
                        value={(selectedSentFilterAndValue ?? {})[filter.value] || ""}
                        onInputChange={(event, newInputValue) => {
                          searchCustomerEmail(newInputValue);
                        }}
                        onChange={(event, newValue) => {
                          const selectedSuggestion = suggestions.find(
                            (suggestion) => suggestion.email === newValue
                          );

                          setSelectedSentFilterAndValue((prev) => ({
                            ...prev,
                            action__contact__email__icontains:
                              selectedSuggestion
                                ? selectedSuggestion.email
                                : "",
                          }));
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={filter.label}
                            variant="outlined"
                          />
                        )}
                      />
                    ) : filter.value ===
                      "action__assignee__username__icontains" ? (
                      <Autocomplete
                        freeSolo
                        options={assigneeSuggestions.map(
                          (option) => option.username
                        )}
                        value={(selectedSentFilterAndValue ?? {})[filter.value] || ""}
                        onInputChange={(event, newInputValue) => {
                          searchAssigneeSuggestions(newInputValue);
                        }}
                        onChange={(event, newValue) => {
                          const selectedSuggestion = assigneeSuggestions.find(
                            (suggestion) => suggestion.username === newValue
                          );

                          setSelectedSentFilterAndValue((prev) => ({
                            ...prev,
                            action__assignee__username__icontains:
                              selectedSuggestion
                                ? selectedSuggestion.username
                                : "",
                          }));
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={filter.label}
                            variant="outlined"
                          />
                        )}
                      />
                    ) : (
                      <TextField
                        label={filter.label}
                        fullWidth
                        value={(selectedSentFilterAndValue ?? {})[filter.value] || ""}
                        onChange={(e) =>
                          handleFilterValueChange(e.target.value, filter.value)
                        }
                      />
                    )}
                  </>
                )}
              </Col>
            </Row>
          ))}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-primary" onClick={resetFilters}>
          Reset
        </Button>
        <Button variant="primary" onClick={handleApplyFilters}>
          Apply
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SentFilterModal;
